import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import {
  IcoChevronLeft,
  IcoChevronRight,
  IcoPencil,
  IcoUser,
} from "../../components/icons";
import { BrandedLayout, ReactionBar } from "../../components/ui";
import { decodeMentionsToHtml } from "../../components/ui/rich-text-mention-extension";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, HighFive, MemberProfile, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";
import { formatDateShort } from "../../utils/format";

type Submission = {
  id: string;
  formId: string;
  formTitle: string;
  periodStart: Date;
  submittedAt: Date;
};

type MemberProfileState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  profile: MemberProfile;
  submissions: Submission[];
  submissionsHasMore: boolean;
  highFives: HighFive[];
  highFivesHasMore: boolean;
};

export async function load(args: LoaderArgs): Promise<MemberProfileState> {
  const orgId = args.params.orgId ?? "";
  const memberId = args.params.memberId ?? "";

  const [authResult, orgsResult, orgResult, profileResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
    rpc.members.getProfile({ workspaceId: orgId, memberId }),
  ]);

  if (!authResult.user) throw new RedirectError("/login");

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Load submissions if viewer has access
  let submissions: Submission[] = [];
  let submissionsHasMore = false;
  if (
    profileResult.profile.isManager ||
    profileResult.profile.isAdmin ||
    profileResult.profile.isSelf
  ) {
    const subResult = await rpc.members.getMemberSubmissions({
      workspaceId: orgId,
      memberId,
      limit: 5,
    });
    submissions = subResult.submissions.map((s) => ({
      id: s.id,
      formId: s.formId,
      formTitle: s.formTitle,
      periodStart: s.periodStart,
      submittedAt: s.submittedAt ? new Date(s.submittedAt) : new Date(),
    }));
    submissionsHasMore = subResult.hasMore;
  }

  // Load high fives
  const hfResult = await rpc.highFives.getForMember({
    workspaceId: orgId,
    memberId,
    limit: 5,
  });

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin: isAdmin(orgResult.currentMember.role),
    hasReports: orgFromList?.hasReports ?? false,
    profile: profileResult.profile,
    submissions,
    submissionsHasMore,
    highFives: hfResult.highFives,
    highFivesHasMore: hfResult.hasMore,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
    profile,
  } = state.value;
  usePageTitle(profile.name ?? "Member Profile");

  const submissions = useSignal<Submission[]>(state.value.submissions);
  const submissionsHasMore = useSignal(state.value.submissionsHasMore);
  const submissionsLoading = useSignal(false);

  const highFives = useSignal<HighFive[]>(state.value.highFives);
  const highFivesHasMore = useSignal(state.value.highFivesHasMore);
  const highFivesLoading = useSignal(false);

  const editingBio = useSignal(false);
  const bioText = useSignal(profile.bio ?? "");
  const bioSaving = useSignal(false);

  const displayName = profile.name || profile.email;

  async function handleSaveBio() {
    if (bioSaving.value) return;
    bioSaving.value = true;
    try {
      await rpc.members.updateBio({
        workspaceId: orgId,
        bio: bioText.value,
      });
      editingBio.value = false;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      bioSaving.value = false;
    }
  }

  async function handleLoadMoreSubmissions() {
    if (submissionsLoading.value) return;
    submissionsLoading.value = true;
    try {
      const result = await rpc.members.getMemberSubmissions({
        workspaceId: orgId,
        memberId: profile.memberId,
        limit: 5,
        offset: submissions.value.length,
      });
      submissions.value = [
        ...submissions.value,
        ...result.submissions.map((s) => ({
          id: s.id,
          formId: s.formId,
          formTitle: s.formTitle,
          periodStart: s.periodStart,
          submittedAt: s.submittedAt ? new Date(s.submittedAt) : new Date(),
        })),
      ];
      submissionsHasMore.value = result.hasMore;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      submissionsLoading.value = false;
    }
  }

  async function handleLoadMoreHighFives() {
    if (highFivesLoading.value) return;
    highFivesLoading.value = true;
    try {
      const result = await rpc.highFives.getForMember({
        workspaceId: orgId,
        memberId: profile.memberId,
        limit: 5,
        offset: highFives.value.length,
      });
      highFives.value = [...highFives.value, ...result.highFives];
      highFivesHasMore.value = result.hasMore;
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      highFivesLoading.value = false;
    }
  }

  const canViewActivity =
    profile.isManager || profile.isAdmin || profile.isSelf;

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="org-chart"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex justify-center">
        <div class="w-full max-w-lg flex flex-col gap-6">
          <a
            href={`/orgs/${orgId}/org-chart`}
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back to People
          </a>

          {/* Profile Header */}
          <div class="flex items-center gap-5">
            <div class="size-16 rounded-full flex items-center justify-center bg-surface-muted overflow-hidden">
              {profile.profilePictureUrl ? (
                <img
                  src={profile.profilePictureUrl}
                  alt={displayName}
                  class="size-16 rounded-full object-cover"
                />
              ) : (
                <IcoUser class="size-8 text-text-muted" />
              )}
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2 flex-wrap">
                <h1 class="text-2xl font-bold text-text">{displayName}</h1>
                {profile.role === "admin" && (
                  <span class="text-xs font-medium px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                    Admin
                  </span>
                )}
              </div>
              <p class="text-text-muted">{profile.email}</p>
              {profile.title && (
                <p class="text-sm text-text-secondary">{profile.title}</p>
              )}
            </div>
          </div>

          {/* Give High Five action */}
          {!profile.isSelf && (
            <a
              href={`/orgs/${orgId}/high-fives?to=${profile.memberId}`}
              class="self-start px-4 py-2 text-sm font-medium rounded-lg bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors"
            >
              Give High Five
            </a>
          )}

          {/* About Section */}
          <section class="rounded-xl border border-border bg-surface p-5 flex flex-col gap-4">
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide">
              About
            </h2>

            {/* Bio */}
            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-text-secondary">Bio</span>
                {profile.isSelf && !editingBio.value && (
                  <button
                    type="button"
                    onClick={() => {
                      bioText.value = profile.bio ?? "";
                      editingBio.value = true;
                    }}
                    class="text-text-muted hover:text-text transition-colors"
                  >
                    <IcoPencil class="size-3.5" />
                  </button>
                )}
              </div>
              {editingBio.value ? (
                <div class="flex flex-col gap-2">
                  <textarea
                    value={bioText.value}
                    onInput={(e) => {
                      bioText.value = (e.target as HTMLTextAreaElement).value;
                    }}
                    rows={3}
                    maxLength={2000}
                    class="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-y"
                    placeholder="Write a short bio..."
                  />
                  <div class="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSaveBio}
                      disabled={bioSaving.value}
                      class="px-3 py-1.5 text-sm font-medium rounded-lg bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors disabled:opacity-50"
                    >
                      {bioSaving.value ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        editingBio.value = false;
                      }}
                      class="px-3 py-1.5 text-sm font-medium rounded-lg text-text-muted hover:text-text hover:bg-surface-muted transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p class="text-sm text-text">
                  {profile.bio || (
                    <span class="text-text-muted italic">No bio yet</span>
                  )}
                </p>
              )}
            </div>

            {/* Manager */}
            {profile.managerName && (
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-text-secondary">
                  Manager
                </span>
                <p class="text-sm text-text">{profile.managerName}</p>
              </div>
            )}

            {/* Direct Reports */}
            {profile.directReportCount > 0 && (
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-text-secondary">
                  Direct Reports
                </span>
                <p class="text-sm text-text">{profile.directReportCount}</p>
              </div>
            )}
          </section>

          {/* Activity Section */}
          <section class="rounded-xl border border-border bg-surface p-5 flex flex-col gap-4">
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide">
              Activity
            </h2>
            {canViewActivity ? (
              <>
                {submissions.value.length > 0 ? (
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead>
                        <tr class="border-b border-border">
                          <th class="text-left py-2 pr-4 font-medium text-text-secondary">
                            Check-in
                          </th>
                          <th class="text-left py-2 pr-4 font-medium text-text-secondary">
                            Submitted
                          </th>
                          <th class="text-left py-2 font-medium text-text-secondary">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.value.map((sub) => (
                          <tr
                            key={sub.id}
                            class="border-b border-border last:border-0"
                          >
                            <td class="py-2 pr-4 text-text">{sub.formTitle}</td>
                            <td class="py-2 pr-4 text-text-muted">
                              {formatDateShort(sub.submittedAt)}
                            </td>
                            <td class="py-2">
                              <a
                                href={`/orgs/${orgId}/forms/${sub.formId}?submission=${sub.id}`}
                                class="text-[var(--brand-600)] hover:underline font-medium"
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p class="text-sm text-text-muted">No submissions yet.</p>
                )}
                {submissionsHasMore.value && (
                  <button
                    type="button"
                    onClick={handleLoadMoreSubmissions}
                    disabled={submissionsLoading.value}
                    class="text-sm font-medium text-[var(--brand-600)] hover:underline disabled:opacity-50 self-start"
                  >
                    {submissionsLoading.value ? "Loading..." : "Load More"}
                  </button>
                )}
              </>
            ) : (
              <p class="text-sm text-text-muted">Activity is private.</p>
            )}
          </section>

          {/* High Fives Section */}
          <section class="rounded-xl border border-border bg-surface p-5 flex flex-col gap-4">
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide">
              High Fives
            </h2>
            {highFives.value.length > 0 ? (
              <div class="flex flex-col gap-3">
                {highFives.value.map((hf) => (
                  <HighFiveCard
                    key={hf.id}
                    highFive={hf}
                    workspaceId={orgId}
                    profileMemberId={profile.memberId}
                  />
                ))}
              </div>
            ) : (
              <p class="text-sm text-text-muted">No high fives yet.</p>
            )}
            {highFivesHasMore.value && (
              <button
                type="button"
                onClick={handleLoadMoreHighFives}
                disabled={highFivesLoading.value}
                class="text-sm font-medium text-[var(--brand-600)] hover:underline disabled:opacity-50 self-start"
              >
                {highFivesLoading.value ? "Loading..." : "Load More"}
              </button>
            )}
          </section>

          {/* Admin Link */}
          {profile.isAdmin && (
            <a
              href={`/orgs/${orgId}/admin/members/${profile.memberId}`}
              class="text-sm font-medium text-[var(--brand-600)] hover:underline"
            >
              Manage member in admin
            </a>
          )}
        </div>
      </div>
    </BrandedLayout>
  );
}

function HighFiveCard({
  highFive,
  workspaceId,
  profileMemberId,
}: {
  highFive: HighFive;
  workspaceId: string;
  profileMemberId: string;
}) {
  const isAuthor = highFive.author.memberId === profileMemberId;
  return (
    <div class="rounded-lg border border-border bg-surface p-4 flex flex-col gap-2">
      <div class="flex items-center gap-1.5 text-sm flex-wrap">
        {isAuthor ? (
          <>
            <span class="text-text-muted">Recognized</span>
            {highFive.recipients.map((r, i) => (
              <span key={r.memberId}>
                {i > 0 && <span class="text-text-subtle">,</span>}{" "}
                <a
                  href={`/orgs/${workspaceId}/members/${r.memberId}/profile`}
                  class="font-semibold text-text hover:underline"
                >
                  {r.name}
                </a>
              </span>
            ))}
          </>
        ) : (
          <>
            <a
              href={`/orgs/${workspaceId}/members/${highFive.author.memberId}/profile`}
              class="font-semibold text-text hover:underline"
            >
              {highFive.author.name}
            </a>
            <span class="text-text-muted">recognized them</span>
          </>
        )}
      </div>
      <div
        class="text-sm text-text-muted [&_.mention]:text-[var(--brand-600)] [&_.mention]:font-medium [&_p]:m-0 [&_p]:leading-relaxed"
        dangerouslySetInnerHTML={{
          __html: decodeMentionsToHtml(highFive.message),
        }}
      />
      <div class="flex items-center justify-between mt-1">
        <span class="text-xs text-text-subtle">
          {formatDateShort(highFive.createdAt)}
        </span>
        <ReactionBar
          workspaceId={workspaceId}
          targetType="high_five"
          targetId={highFive.id}
          compact
        />
      </div>
    </div>
  );
}
